import { useState } from "react";
import { SlidersHorizontal, ShoppingBag, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const STATUS_OPTS = [
  { v: "", l: "Any" },
  { v: "pending", l: "Pending" },
  { v: "processing", l: "Processing" },
  { v: "completed", l: "Completed" },
];

export const EMPTY_DEALER_FILTERS = { neverOrdered: false, notOrderedSince: "", orderStatus: "" };

export function applyDealerContactFilters(contacts, f) {
  return contacts.filter((c) => {
    const n = Number(c.order_count || 0);
    if (f.neverOrdered && n > 0) return false;
    if (f.notOrderedSince && !(n > 0 && (c.last_order_date || "") < f.notOrderedSince)) return false;
    if (f.orderStatus && !(c.order_statuses || []).includes(f.orderStatus)) return false;
    return true;
  });
}

function Chip({ label, onRemove, testId }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 text-violet-800 px-2.5 py-1 text-[11px] font-medium hover:bg-violet-100"
      data-testid={testId}
      aria-label={`Remove ${label}`}
    >
      {label}
      <X size={11} />
    </button>
  );
}

export function DealerContactFilters({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });
  const active = [value.neverOrdered, value.notOrderedSince, value.orderStatus].filter(Boolean).length;

  return (
    <div className="mb-3" data-testid="dealer-crm-filters">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-xl border px-3 h-9 text-sm font-medium transition-colors ${
            open ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
          }`}
          data-testid="crm-filters-toggle"
        >
          <SlidersHorizontal size={14} /> Filters
          {active > 0 && <span className="ml-1 text-[10px] rounded-full bg-white/25 px-1.5 py-0.5 leading-none">{active}</span>}
        </button>
        {value.neverOrdered && <Chip label="Never ordered" onRemove={() => set({ neverOrdered: false })} testId="crm-activechip-never-ordered" />}
        {value.notOrderedSince && <Chip label={`No order since ${value.notOrderedSince}`} onRemove={() => set({ notOrderedSince: "" })} testId="crm-activechip-not-since" />}
        {value.orderStatus && <Chip label={`Order status: ${value.orderStatus}`} onRemove={() => set({ orderStatus: "" })} testId="crm-activechip-order-status" />}
      </div>

      {open && (
        <div className="mt-2 rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 space-y-3" data-testid="crm-filters-panel">
          <button
            type="button"
            onClick={() => set({ neverOrdered: !value.neverOrdered, notOrderedSince: "" })}
            aria-pressed={value.neverOrdered}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              value.neverOrdered ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
            }`}
            data-testid="crm-subfilter-never-ordered"
          >
            <ShoppingBag size={13} /> Never ordered
          </button>

          <div className={`flex items-center gap-2 ${value.neverOrdered ? "opacity-40 pointer-events-none" : ""}`}>
            <span className="text-[11px] text-zinc-500 shrink-0 w-24">No order since</span>
            <Input type="date" value={value.notOrderedSince} onChange={(e) => set({ notOrderedSince: e.target.value })} className="h-9 rounded-lg text-xs" data-testid="crm-subfilter-not-since" />
            <span className="text-[10px] text-zinc-400 leading-tight">last order was before this date</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-zinc-500 shrink-0 w-24">Order status</span>
            {STATUS_OPTS.map((o) => (
              <button
                key={o.v || "any"}
                type="button"
                onClick={() => set({ orderStatus: o.v })}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  value.orderStatus === o.v ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                }`}
                data-testid={`crm-order-status-${o.v || "any"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
